/**
 * Synthetic Milestone Evaluator (DEV-681 Phase 3, Path C)
 *
 * Pure function that derives a user's achieved-milestones set by
 * inspecting current state from existing API surfaces. No backend
 * MilestoneEvaluator service required — the answer is computed live
 * from data that already exists.
 *
 * This is the Path C interim solution. When DEV-508 ships its proper
 * server-side MilestoneEvaluator with domain events, the runtime will
 * be replaced but the Tasks tab UI doesn't change — the contract is
 * just "give me a Set<MilestoneKey> of achieved milestones."
 *
 * Design constraints:
 *   - Pure: no I/O, no React, no DOM. Same inputs → same outputs.
 *   - Best-effort: milestones whose underlying state isn't yet exposed
 *     in the frontend (e.g. 2FA enabled, EULA accepted) simply don't
 *     get marked complete. Users see them as outstanding tasks; once
 *     the relevant API field surfaces, the detector wires up here and
 *     the milestone clears automatically. No UI churn.
 *   - Conservative: when in doubt, treat as not-yet-done. Better to
 *     show a task that's already complete than to falsely mark
 *     incomplete milestones complete.
 *
 * See `src/Data/Onboarding/archetypeConfig.ts` for the milestone keys
 * and `src/Pages/ContentApprovals/TasksTab.tsx` for the consumer.
 */

import type { MilestoneKey } from './archetypeConfig';
import { EULA_CURRENT_VERSION } from '../../content/legal/eulaConstants';
import { profileCompletion } from './profileCompletion';

/**
 * Inputs the evaluator reads. Each field maps to data already fetched
 * by an existing useApi* hook elsewhere in the app, so the Tasks tab
 * can just thread these in via props.
 */
export interface MilestoneEvaluatorInputs {
  /** Current user's GUID, normalized to UPPER. */
  currentUserGuid: string;

  /**
   * The user's row from `useApiSecurityData().securityUsers`, after
   * `securityUserToSeedShape`. Carries display_name, profile_image_url,
   * notes (bio), last_login_at, etc.
   */
  currentUser?: Record<string, any> | null;

  /**
   * All community memberships from `useApiSecurityData()`.
   *
   * Currently unused — community membership is mandatory at signup
   * (L4E is community-centric by design), so it isn't a milestone the
   * user can fail to achieve. The earlier `first_follow` detector
   * misused this as a proxy for the unbuilt follow concept; that's
   * been removed. Field retained for now so the prop wiring is in
   * place for when DEV-540 (Follow/Unfollow) ships and a real follow
   * detector reads from `user_follows` instead.
   */
  communityMemberships?: Record<string, any>[];

  /**
   * All project ACL rows from `useApiProjectAcl()` or equivalent.
   * Filtered by user inside the evaluator. Used to detect roles
   * (volunteer / owner / etc.).
   */
  projectAcl?: Record<string, any>[];

  /**
   * All projects (from `useApiProjects()`). Used to detect project
   * creation by checking owner/creator fields.
   */
  projects?: Record<string, any>[];
}

/**
 * Result of evaluation: which milestones the user has achieved.
 *
 * The Tasks tab reads this via `Set.has(milestone)`. A milestone NOT
 * in the set means either (a) the user genuinely hasn't done it, or
 * (b) the platform can't currently detect it. The Tasks tab can't
 * tell the difference, which is why we use the "show CTA, let the
 * user complete it manually" UX rather than a hard-fail "you can't
 * advance" gate.
 */
export type AchievedMilestonesSet = Set<MilestoneKey>;

// ── Per-milestone detectors ─────────────────────────────────

// `hasFirstLogin` detector removed — see milestone removal comment
// below (in evaluateMilestones). The first_login slug remains
// reserved in MILESTONE_KEYS in case we revive it for a different
// purpose (e.g., a returning-user campaign nudge).

function hasEnable2fa(inputs: MilestoneEvaluatorInputs): boolean {
  // DEV-906 — derived from TotpEnabled on SecurityUser. The mapper
  // exposes it as `is_two_factor_enabled` on the seed shape.
  return Boolean(inputs.currentUser?.is_two_factor_enabled);
}

function hasAcceptEula(inputs: MilestoneEvaluatorInputs): boolean {
  // DEV-906 — recurring milestone semantics. The user must have
  // explicitly accepted at least the CURRENT version. Older
  // versions don't satisfy the milestone (Skip's directive: EULA
  // updates require re-acceptance). NULL version (pre-versioning
  // accepts) is also treated as needs-re-acceptance.
  const acceptedAt = inputs.currentUser?.eula_accepted_at;
  const acceptedVersion = inputs.currentUser?.eula_version_accepted;
  if (!acceptedAt) return false;
  if (typeof acceptedVersion !== 'number') return false;
  return acceptedVersion >= EULA_CURRENT_VERSION;
}

function hasCompleteProfile(inputs: MilestoneEvaluatorInputs): boolean {
  // §7.17 (DEV-1484): THE singular predicate — avatar + cover photo +
  // short bio (profileCompletion helper), plus display name (server
  // lockstep: OnboardingRung.RungBasicsCompleteAsync). Cover added
  // 2026-07-14; already-promoted users never regress (one-way rung) and
  // emitted Roots events are never revoked.
  const u = inputs.currentUser;
  if (!u) return false;
  const hasName = Boolean(u.display_name && String(u.display_name).trim());
  return hasName && profileCompletion(u).complete;
}

// `first_follow` and `first_cross_community` detectors removed in the
// DEV-681 Phase 3 cleanup pass. Earlier drafts mapped them to community
// membership state, but that conflates two distinct concepts:
//   - Membership = formal community affiliation, MANDATORY at signup.
//     Every L4E user is in at least one community by definition; it's
//     a precondition of having an account, not a milestone to achieve.
//   - Follow = lightweight asymmetric subscription (DEV-540, To Do).
//     Doesn't yet exist as a feature.
// When DEV-540 ships, add a `hasFirstFollow()` detector here that reads
// from `user_follows` (the polymorphic follows table per DEV-540 spec) —
// NOT from community memberships.

// `first_volunteer` detector REMOVED (chunk B, §7.16 vestige cleanup):
// the Impact Signature reveal keys on Contributor promotion, and the
// radar/growth/KPI features are Contributor baseline (archetypeConfig).
// Volunteering earns impact through the emission spine, not a milestone.

function hasFirstProjectCreated(inputs: MilestoneEvaluatorInputs): boolean {
  // User created a project means they own at least one. Owner detection
  // checks both `created_by` (audit field) and `owner_user_guid` (current
  // owner) so re-assignment of a project still counts the original
  // creator's milestone.
  const guid = inputs.currentUserGuid.toUpperCase();
  if (!guid) return false;
  const projects = inputs.projects || [];
  return projects.some((p: any) => {
    const creator = String(
      p?.created_by || p?.createdBy || p?.owner_user_guid || p?.ownerUserGuid || '',
    ).toUpperCase();
    return creator === guid;
  });
}

// ── Detectors NOT yet wired (defer until backend exposes state) ──
// `enable_2fa` — TOTP table exists (migration 013) but `is_2fa_enabled`
//   isn't on the SecurityUserResponse DTO yet. Until that lands, the
//   milestone shows as outstanding even for users who have 2FA enabled.
//   That's acceptable: they'll see "Enable 2FA" in their Tasks tab
//   with a CTA to /profile, where they can verify it's already on.
//   Once the flag is exposed, add a `hasEnable2fa()` detector here.
//
// `accept_eula` — same situation. No `eula_accepted_at` field exposed yet.
//
// `first_comment` — content engagement query (across articles/chronicles/
//   issues) — non-trivial. Defer.
//
// `first_role_completed` — role completion lifecycle isn't fully built yet.
//
// `first_workspace_created` — workspace ownership query, similar to
//   project creation. Easy to add when needed.
//
// `earned_inviter_status` — depends on DEV-672 trust scoring system
//   which isn't shipped. Defer.

// ── Main evaluator ──────────────────────────────────────────

/**
 * Evaluate all known milestones against the inputs. Returns a Set
 * containing only milestones where the underlying state confirms
 * completion. Undetectable milestones (those without a wired detector)
 * are intentionally absent from the set so the Tasks tab can surface
 * them as outstanding work.
 */
export function evaluateMilestones(
  inputs: MilestoneEvaluatorInputs,
): AchievedMilestonesSet {
  const achieved = new Set<MilestoneKey>();

  // `first_login` detector intentionally not invoked — milestone
  // was removed from the Explorer progression because every user
  // looking at the Tasks tab has by definition already logged in.
  // The slug + detector function are retained in MILESTONE_KEYS for
  // potential future reuse (e.g. a returning-user nudge campaign).
  if (hasCompleteProfile(inputs)) achieved.add('complete_profile');
  if (hasEnable2fa(inputs)) achieved.add('enable_2fa');
  if (hasAcceptEula(inputs)) achieved.add('accept_eula');
  if (hasFirstProjectCreated(inputs)) achieved.add('first_project_created');

  return achieved;
}
